import { useEffect, useRef } from 'react';
import { getActive, setActive, clearActive } from '@Utils/focusManager';

const attemptFocus = (element) => {
  if (!element || typeof element.focus !== 'function' || element.disabled || element.tabIndex === -1 || element.offsetParent === null) {
    return false;
  }

  element.focus();
  return document.activeElement === element || document.activeElement === element.firstChild;
};

// usePostActionFocus
// - id: unique id string for this control
// - dependentKeys: array of dependencies which trigger focus attempts (e.g., selectedValue, resultsPerPage)
// - forwardedRef: optional ref to the underlying DOM element (preferred)
// - avoidIfActiveSelector: optional CSS selector; if document.activeElement is inside this selector, skip focusing
export function usePostActionFocus({ id, dependentKeys = [], forwardedRef = null, avoidIfActiveSelector = null }) {
  const ref = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    const active = getActive();
    if (active !== id) return undefined;

    // If caller requested avoiding focus when a particular area is active, bail out
    if (avoidIfActiveSelector) {
      const activeEl = document.activeElement;
      if (activeEl && activeEl.closest && activeEl.closest(avoidIfActiveSelector)) {
        clearActive();
        return undefined;
      }
    }

    const tryFocusOnce = () => {
      // If this hook instance is no longer the active one, abort and clear its timers
      if (getActive() !== id) {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
        return false;
      }

      // Try forwarded ref first -> hook ref -> sub-selectors
      const el = ref.current;
      const focusable = el?.querySelector?.('button, select, input, textarea, [tabindex]:not([tabindex="-1"])');

      if (attemptFocus(forwardedRef?.current) || attemptFocus(el) || attemptFocus(focusable)) {
        clearActive();
        return true;
      }

      return false;
    };

    const timers = [];
    const delays = [0, 50, 100, 200, 500];

    const runRetry = (delay) => {
      const t = setTimeout(() => {
        if (tryFocusOnce()) {
          timers.forEach(clearTimeout);
        }
      }, delay);
      timers.push(t);
    };

    delays.forEach(runRetry);
    timersRef.current = timers;

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // dependentKeys intentionally omitted from exhaustive-deps lint rule to allow array control
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependentKeys);

  const markActive = () => setActive(id);

  return { ref, markActive };
}

export default usePostActionFocus;
